import { useEffect, useState } from "react";
import type {
  PartyCommand,
  PartyView,
  WorldSnapshot,
} from "@frontier/protocol/contracts";
import "./party.css";
import { pressHandlers } from "./press-handlers";
import { pilotLabel } from "./pilot-label";

interface Props {
  view: PartyView;
  world: WorldSnapshot;
  playerId: string;
  targetId: string;
  connected: boolean;
  onCommand: (command: PartyCommand) => void;
}

export function PartyControls({
  view,
  world,
  playerId,
  targetId,
  connected,
  onCommand,
}: Props) {
  const [open, setOpen] = useState(false);
  const party = view.party;
  const leader = party?.leaderId === playerId;
  const target = world.npcs.find((npc) => npc.id === targetId && !npc.dead);
  const candidates = view.availablePilots.filter(
    (pilot) =>
      !party?.members.some((member) => member.playerId === pilot.playerId),
  );
  const full = (party?.members.length ?? 0) >= 4;

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);

  return (
    <div className="party-controls">
      <button
        className={`party-button ${view.invitations.length ? "has-invitation" : ""}`}
        aria-label="Группа"
        aria-expanded={open}
        {...pressHandlers(() => setOpen(!open))}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          aria-hidden="true"
        >
          <circle cx="9" cy="7" r="3" />
          <path d="M3 20v-3a6 6 0 0 1 12 0v3M16 5a3 3 0 0 1 0 6m2 3a6 6 0 0 1 3 6" />
        </svg>
        <small>{party?.members.length ?? 0}/4</small>
        {view.invitations.length > 0 && <i>{view.invitations.length}</i>}
      </button>
      {open && (
        <section
          className="party-panel panel"
          role="dialog"
          aria-label="Группа пилотов"
          aria-modal="false"
        >
          <div className="party-heading">
            <b>ГРУППА · {party?.members.length ?? 0}/4</b>
            <button
              aria-label="Закрыть группу"
              {...pressHandlers(() => setOpen(false))}
            >
              ×
            </button>
          </div>
          {!connected && <p>Восстанавливаем связь с группой…</p>}
          {view.invitations.length > 0 && (
            <section className="party-section">
              <span className="eyebrow">ПРИГЛАШЕНИЯ</span>
              {view.invitations.map((invitation) => (
                <div className="party-invitation" key={invitation.partyId}>
                  <b>
                    {pilotLabel(invitation.leaderName, invitation.leaderId)}
                  </b>
                  <div>
                    <button
                      disabled={!connected || party !== null}
                      {...pressHandlers(() =>
                        onCommand({
                          type: "accept",
                          partyId: invitation.partyId,
                        }),
                      )}
                    >
                      Принять
                    </button>
                    <button
                      disabled={!connected}
                      {...pressHandlers(() =>
                        onCommand({
                          type: "decline",
                          partyId: invitation.partyId,
                        }),
                      )}
                    >
                      Отклонить
                    </button>
                  </div>
                </div>
              ))}
              {party && <p>Для принятия выйди из текущей группы.</p>}
            </section>
          )}
          {party ? (
            <>
              <ul className="party-members">
                {party.members.map((member) => (
                  <li key={member.playerId} data-player-id={member.playerId}>
                    <div>
                      <b>
                        {pilotLabel(member.name, member.playerId)}
                        {member.playerId === playerId ? " · ты" : ""}
                      </b>
                      <small>
                        {member.online
                          ? member.playerId === party.leaderId
                            ? "Командир"
                            : "В группе"
                          : "Вне связи · ждём возвращения"}
                      </small>
                    </div>
                    {leader && member.playerId !== playerId && (
                      <button
                        disabled={!connected}
                        aria-label={`Исключить ${pilotLabel(member.name, member.playerId)}`}
                        {...pressHandlers(() =>
                          onCommand({
                            type: "kick",
                            playerId: member.playerId,
                          }),
                        )}
                      >
                        Исключить
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <section className="party-section">
                <span className="eyebrow">ОБЩАЯ ЦЕЛЬ</span>
                {party.marker ? (
                  <div className="party-marked-target">
                    <b>⌖ {party.marker.label}</b>
                    <button
                      disabled={!connected}
                      {...pressHandlers(() =>
                        onCommand({ type: "clearMarker" }),
                      )}
                    >
                      Убрать метку
                    </button>
                  </div>
                ) : null}
                <button
                  className="party-mark-button"
                  disabled={!connected || !target}
                  {...pressHandlers(() => {
                    if (target)
                      onCommand({ type: "mark", targetId: target.id });
                  })}
                >
                  {target
                    ? `Отметить: ${target.name}`
                    : "Выбери цель на карте для общей метки"}
                </button>
              </section>
            </>
          ) : (
            <p>Пригласи пилота</p>
          )}
          <section className="party-section">
            <span className="eyebrow">ПИЛОТЫ В СЕТИ</span>
            {candidates.length > 0 ? (
              <ul className="party-candidates">
                {candidates.map((pilot) => (
                  <li key={pilot.playerId}>
                    <b>{pilotLabel(pilot.name, pilot.playerId)}</b>
                    <button
                      disabled={
                        !connected || full || (party !== null && !leader)
                      }
                      aria-label={`Пригласить ${pilotLabel(pilot.name, pilot.playerId)}`}
                      {...pressHandlers(() =>
                        onCommand({ type: "invite", targetId: pilot.playerId }),
                      )}
                    >
                      Пригласить
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Других пилотов пока нет.</p>
            )}
          </section>
          {party && (
            <button
              className="party-leave"
              disabled={!connected}
              {...pressHandlers(() => onCommand({ type: "leave" }))}
            >
              Выйти из группы
            </button>
          )}
        </section>
      )}
    </div>
  );
}
